/**
 * Cart Store
 *
 * Estado global do carrinho usando Zustand.
 * Gerencia o carrinho ativo, estado do drawer e preferencias.
 *
 * @since 2025-12-16
 * @epic INIT-0045
 * @module tmf_store
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ==========================================
// TYPES
// ==========================================

interface CartItem {
  id: number;
  productId: number;
  productName: string;
  productCode?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

interface CartState {
  // Carrinho atual
  currentCartId: number | null;
  currentStoreId: number | null;
  sessionId: string | null;

  // Itens locais (cache otimista antes de sincronizar)
  localItems: CartItem[];

  // UI State
  isCartOpen: boolean;
  isLoading: boolean;

  // Mini cart (resumo no header)
  itemCount: number;
  subtotal: number;

  // Actions - Cart Identity
  setCartId: (cartId: number | null) => void;
  setStoreId: (storeId: number | null) => void;
  setSessionId: (sessionId: string) => void;
  clearCart: () => void;

  // Actions - UI
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  setLoading: (loading: boolean) => void;

  // Actions - Local Items (cache otimista)
  addLocalItem: (item: CartItem) => void;
  updateLocalItem: (productId: number, quantity: number) => void;
  removeLocalItem: (productId: number) => void;
  setLocalItems: (items: CartItem[]) => void;
  clearLocalItems: () => void;

  // Actions - Summary
  updateSummary: (itemCount: number, subtotal: number) => void;

  // Actions - Reset
  reset: () => void;
}

// ==========================================
// INITIAL STATE
// ==========================================

const initialState = {
  currentCartId: null,
  currentStoreId: null,
  sessionId: null,
  localItems: [],
  isCartOpen: false,
  isLoading: false,
  itemCount: 0,
  subtotal: 0,
};

// ==========================================
// STORE
// ==========================================

export const useCartStore = create<CartState>()(
  persist(
    (set, _get) => ({
      // Initial State
      ...initialState,

      // Cart Identity Actions
      setCartId: (cartId) => set({ currentCartId: cartId }),

      setStoreId: (storeId) => set({ currentStoreId: storeId }),

      setSessionId: (sessionId) => set({ sessionId }),

      clearCart: () =>
        set({
          currentCartId: null,
          localItems: [],
          itemCount: 0,
          subtotal: 0,
        }),

      // UI Actions
      openCart: () => set({ isCartOpen: true }),

      closeCart: () => set({ isCartOpen: false }),

      toggleCart: () => set((state) => ({ isCartOpen: !state.isCartOpen })),

      setLoading: (loading) => set({ isLoading: loading }),

      // Local Items Actions (cache otimista)
      addLocalItem: (item) =>
        set((state) => {
          const existingIndex = state.localItems.findIndex(
            (i) => i.productId === item.productId
          );

          if (existingIndex >= 0) {
            // Atualiza quantidade do item existente
            const updatedItems = [...state.localItems];
            updatedItems[existingIndex] = {
              ...updatedItems[existingIndex],
              quantity: updatedItems[existingIndex].quantity + item.quantity,
              totalPrice:
                (updatedItems[existingIndex].quantity + item.quantity) *
                item.unitPrice,
            };
            return {
              localItems: updatedItems,
              itemCount: state.itemCount + item.quantity,
              subtotal: state.subtotal + item.quantity * item.unitPrice,
            };
          }

          // Adiciona novo item
          return {
            localItems: [...state.localItems, item],
            itemCount: state.itemCount + item.quantity,
            subtotal: state.subtotal + item.totalPrice,
          };
        }),

      updateLocalItem: (productId, quantity) =>
        set((state) => {
          const item = state.localItems.find((i) => i.productId === productId);
          if (!item) return state;

          const quantityDiff = quantity - item.quantity;
          const priceDiff = quantityDiff * item.unitPrice;

          if (quantity <= 0) {
            // Remove item se quantidade for 0 ou negativa
            return {
              localItems: state.localItems.filter((i) => i.productId !== productId),
              itemCount: state.itemCount - item.quantity,
              subtotal: state.subtotal - item.totalPrice,
            };
          }

          const updatedItems = state.localItems.map((i) =>
            i.productId === productId
              ? { ...i, quantity, totalPrice: quantity * i.unitPrice }
              : i
          );

          return {
            localItems: updatedItems,
            itemCount: state.itemCount + quantityDiff,
            subtotal: state.subtotal + priceDiff,
          };
        }),

      removeLocalItem: (productId) =>
        set((state) => {
          const item = state.localItems.find((i) => i.productId === productId);
          if (!item) return state;

          return {
            localItems: state.localItems.filter((i) => i.productId !== productId),
            itemCount: state.itemCount - item.quantity,
            subtotal: state.subtotal - item.totalPrice,
          };
        }),

      setLocalItems: (items) => {
        const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
        const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
        set({ localItems: items, itemCount, subtotal });
      },

      clearLocalItems: () =>
        set({
          localItems: [],
          itemCount: 0,
          subtotal: 0,
        }),

      // Summary Actions
      updateSummary: (itemCount, subtotal) => set({ itemCount, subtotal }),

      // Reset
      reset: () => set(initialState),
    }),
    {
      name: 'tmf-cart-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persistir apenas dados essenciais
        currentCartId: state.currentCartId,
        currentStoreId: state.currentStoreId,
        sessionId: state.sessionId,
      }),
    }
  )
);

// ==========================================
// CONVENIENCE FUNCTIONS
// ==========================================

/**
 * Gera um session ID unico para o carrinho
 */
export function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Obtem ou gera session ID
 */
export function getOrCreateSessionId(): string {
  const state = useCartStore.getState();
  if (state.sessionId) {
    return state.sessionId;
  }

  const newSessionId = generateSessionId();
  useCartStore.getState().setSessionId(newSessionId);
  return newSessionId;
}

/**
 * Formata valor monetario
 */
export function formatPrice(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

// ==========================================
// SELECTORS
// ==========================================

export const cartSelectors = {
  hasItems: (state: CartState) => state.itemCount > 0,
  isEmpty: (state: CartState) => state.itemCount === 0,
  isActive: (state: CartState) => state.currentCartId !== null,
  getItem: (state: CartState, productId: number) =>
    state.localItems.find((i) => i.productId === productId),
  getItemQuantity: (state: CartState, productId: number) =>
    state.localItems.find((i) => i.productId === productId)?.quantity ?? 0,
};
